// RosterCalendar — 月曆檢視 / Phase 1B 排班 UI。
//
// 月格 6 weeks × 7 days，第一格從 calendar-month 起始週的星期一開始。
// 點 cell → 開 drawer，可看當天的排班 list、新增 / 編輯 / 取消。
//
// 上方有 site filter（all sites / 單一案場），切換 site 重新拉資料；
// 月份切換 ←/→ 也會重新拉。
//
// 「人力差距」徽章：每個 cell 算 (sum of shifts.officer_count for shifts
// active that weekday) − (count of roster_entries in 'scheduled' /
// 'confirmed' for that day in scope)。負值或零 = 人力滿足；正值 = 還少
// 幾個人，cell 角落顯示橙色徽章。

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Plus, RefreshCw, ServerCrash, Trash2, Users, Sparkles } from "lucide-react";
import PageHeader from "../components/PageHeader.js";
import EmptyState from "../components/EmptyState.js";
import Drawer from "../components/Drawer.js";
import { Field } from "../components/Field.js";
import { useConfirmDialog } from "../components/ConfirmDialog.js";
import {
  listRoster, createRoster, patchRoster, cancelRoster,
  listScheduleOfficers, generateRoster, friendlyRosterError,
  type RosterEntry, type RosterStatus, type OfficerPickerRow,
  type GenerateRosterResponse,
} from "../lib/rosterApi.js";
import { listShifts, type ShiftRow } from "../lib/shiftsApi.js";
import { listSites, type SiteRow } from "../lib/sitesApi.js";
import {
  listExceptions, createException, patchException, deleteException,
  friendlyExceptionsError,
  type ScheduleException, type ExceptionKind,
} from "../lib/exceptionsApi.js";
import { useDashboardSession } from "../auth/useDashboardSession.js";

const STATUS_LABEL: Record<RosterStatus, string> = {
  scheduled: "已排班", confirmed: "已確認", swapped: "已換班", cancelled: "已取消",
};
const STATUS_BADGE: Record<RosterStatus, string> = {
  scheduled: "badge-default", confirmed: "badge-success", swapped: "badge-warning", cancelled: "badge-danger",
};
const KIND_LABEL: Record<ExceptionKind, string> = {
  holiday: "假日", typhoon: "颱風", closure: "案場關閉", extra_coverage: "加開", custom: "其他",
};
const KIND_RIBBON: Record<ExceptionKind, string> = {
  holiday: "bg-danger/15 text-danger",
  typhoon: "bg-warning/15 text-warning",
  closure: "bg-text-muted/15 text-text-secondary",
  extra_coverage: "bg-accent/15 text-accent",
  custom: "bg-bg-raised text-text-secondary",
};

interface CellData {
  date: string;          // YYYY-MM-DD
  inMonth: boolean;
  entries: RosterEntry[];
  exceptions: ScheduleException[];
  required: number;      // sum of shifts.officer_count active that weekday
  scheduled: number;     // count of non-cancelled entries
}

export default function RosterCalendar(): React.ReactElement {
  const session = useDashboardSession();
  const tenantParam = session.super_admin ? session.acting_tenant_id ?? null : null;
  const canWrite = session.scopes.includes("write:schedule") || session.super_admin === true;

  const [cursor, setCursor] = useState<Date>(() => firstOfMonth(new Date()));
  const [siteFilter, setSiteFilter] = useState<string>("");
  const [sites, setSites] = useState<SiteRow[]>([]);
  const [shifts, setShifts] = useState<ShiftRow[]>([]);
  const [officers, setOfficers] = useState<OfficerPickerRow[]>([]);
  const [entries, setEntries] = useState<RosterEntry[] | null>(null);
  const [exceptions, setExceptions] = useState<ScheduleException[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [openDate, setOpenDate] = useState<string | null>(null);
  const [exceptionModal, setExceptionModal] = useState<null | { mode: "new"; date: string } | { mode: "edit"; row: ScheduleException }>(null);
  const [genOpen, setGenOpen] = useState(false);

  // bootstrap: load shifts + sites + officers once per tenant.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [shiftRes, siteRes, officerList] = await Promise.all([
          listShifts(tenantParam),
          listSites({ tenantId: tenantParam, status: "active", limit: 500 }),
          listScheduleOfficers(tenantParam),
        ]);
        if (cancelled) return;
        setShifts(shiftRes.items.filter((s) => s.status === "active"));
        setSites(siteRes.items);
        setOfficers(officerList);
      } catch (e) { if (!cancelled) setErr(friendlyRosterError(e)); }
    })();
    return () => { cancelled = true; };
  }, [tenantParam]);

  const range = useMemo(() => monthGridRange(cursor), [cursor]);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [rosterRes, excepRes] = await Promise.all([
        listRoster({ tenantId: tenantParam, from: range.from, to: range.to, siteId: siteFilter || null }),
        listExceptions({ tenantId: tenantParam, from: range.from, to: range.to, siteId: siteFilter || null }),
      ]);
      setEntries(rosterRes.items);
      setExceptions(excepRes.items);
    } catch (e) { setErr(friendlyRosterError(e)); }
    finally { setLoading(false); }
  }, [tenantParam, range.from, range.to, siteFilter]);
  useEffect(() => { void load(); }, [load]);

  const cells = useMemo<CellData[]>(() => {
    const arr: CellData[] = [];
    const start = new Date(range.from + "T00:00:00Z");
    for (let i = 0; i < 42; i++) {
      const d = new Date(start.getTime());
      d.setUTCDate(start.getUTCDate() + i);
      const date = ymd(d);
      const inMonth = d.getUTCMonth() === cursor.getUTCMonth();
      const dayEntries = (entries ?? []).filter((e) => e.work_date === date);
      const dayExceptions = exceptions.filter((ex) => {
        const upper = ex.extends_until ?? ex.exception_date;
        return ex.exception_date <= date && date <= upper;
      });
      const weekdayBit = (d.getUTCDay() + 6) % 7;  // 0=Mon...6=Sun (matches days_mask)
      const requiredBase = shifts.reduce((sum, s) =>
        ((s.days_mask & (1 << weekdayBit)) !== 0) ? sum + s.officer_count : sum, 0);
      // 若當天有 auto_skip 例外（且範圍涵蓋本案場 / 全 tenant）→ required 視為 0。
      const skipped = dayExceptions.some((ex) => ex.auto_skip === 1 && (ex.kind === "holiday" || ex.kind === "typhoon" || ex.kind === "closure"));
      const required = skipped ? 0 : requiredBase;
      const scheduled = dayEntries.filter((e) => e.status !== "cancelled").length;
      arr.push({ date, inMonth, entries: dayEntries, exceptions: dayExceptions, required, scheduled });
    }
    return arr;
  }, [range.from, cursor, entries, exceptions, shifts]);

  const monthLabel = `${cursor.getUTCFullYear()} 年 ${cursor.getUTCMonth() + 1} 月`;
  const goPrev = () => setCursor(addMonths(cursor, -1));
  const goNext = () => setCursor(addMonths(cursor, 1));
  const goToday = () => setCursor(firstOfMonth(new Date()));

  const openDayEntries = openDate ? (entries ?? []).filter((e) => e.work_date === openDate) : [];
  const openDayExceptions = openDate
    ? exceptions.filter((ex) => {
        const upper = ex.extends_until ?? ex.exception_date;
        return ex.exception_date <= openDate && openDate <= upper;
      })
    : [];

  return (
    <>
      <PageHeader
        title="月排班"
        description="點任一格新增 / 編輯該日排班；橘色徽章表示人力缺口。"
        actions={
          <div className="flex items-center gap-2">
            <button onClick={goPrev} className="btn-secondary text-sm" aria-label="上一月"><ChevronLeft className="h-4 w-4" /></button>
            <button onClick={goToday} className="btn-secondary text-sm">本月</button>
            <button onClick={goNext} className="btn-secondary text-sm" aria-label="下一月"><ChevronRight className="h-4 w-4" /></button>
            <button className="btn-secondary text-sm" onClick={() => void load()} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> 重新整理
            </button>
            {canWrite && (
              <button className="btn-primary text-sm" onClick={() => setGenOpen(true)}>
                <Sparkles className="h-4 w-4" /> 自動生成
              </button>
            )}
          </div>
        }
      />

      <div className="card mb-3 p-3 flex flex-wrap items-center gap-3 text-sm">
        <span className="text-text-secondary">{monthLabel}</span>
        <span className="text-text-muted">·</span>
        <label className="flex items-center gap-2">
          <span className="text-text-secondary">案場：</span>
          <select className="select" value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)}>
            <option value="">全部案場</option>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
      </div>

      {err && (
        <EmptyState icon={ServerCrash} title="排班載入失敗" description={err}
          action={<button className="btn-primary text-sm" onClick={() => void load()}>再試一次</button>} />
      )}

      {!err && (
        <div className="card overflow-hidden">
          <div className="grid grid-cols-7 border-b border-border bg-bg-surface text-xs text-text-secondary">
            {["一", "二", "三", "四", "五", "六", "日"].map((d) => (
              <div key={d} className="px-2 py-2 text-center font-medium">星期{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {cells.map((c) => {
              const gap = c.required - c.scheduled;
              return (
                <button key={c.date}
                  onClick={() => setOpenDate(c.date)}
                  className={`min-h-[108px] border-b border-r border-border/60 px-2 py-1.5 text-left transition-colors hover:bg-bg-hover ${
                    c.inMonth ? "bg-bg-base" : "bg-bg-base/40 text-text-muted"
                  }`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-mono ${c.inMonth ? "text-text-primary" : "text-text-muted"}`}>{Number(c.date.slice(8, 10))}</span>
                    {gap > 0 && c.inMonth && (
                      <span className="text-[10px] rounded-full bg-warning/15 text-warning px-1.5 py-0.5 font-medium">缺{gap}</span>
                    )}
                  </div>
                  <div className="mt-1 space-y-0.5">
                    {c.exceptions.slice(0, 2).map((ex) => (
                      <div key={ex.id} className={`truncate rounded px-1 py-0.5 text-[10px] ${KIND_RIBBON[ex.kind]}`}>
                        <span className="mr-1">{KIND_LABEL[ex.kind]}</span>
                        <span>{ex.label}</span>
                      </div>
                    ))}
                    {c.entries.slice(0, Math.max(0, 3 - c.exceptions.slice(0, 2).length)).map((e) => (
                      <div key={e.id} className={`truncate rounded px-1 py-0.5 text-[10px] ${
                        e.status === "cancelled" ? "bg-danger/10 text-danger line-through" : "bg-accent/10 text-accent"
                      }`}>
                        <span className="font-mono">{e.shift_start ?? "??"}</span>
                        <span className="ml-1">{e.officer_name ?? e.officer_id.slice(0, 6)}</span>
                      </div>
                    ))}
                    {(c.entries.length + c.exceptions.length) > 3 && (
                      <div className="text-[10px] text-text-muted">+{(c.entries.length + c.exceptions.length) - 3} 更多…</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <Drawer
        open={!!openDate}
        onClose={() => setOpenDate(null)}
        title={openDate ? `${openDate} 排班` : ""}
        description="當日所有排班；點任一條進編輯，下方按鈕新增。"
      >
        {openDate && (
          <DayDrawer
            date={openDate}
            entries={openDayEntries}
            exceptions={openDayExceptions}
            shifts={shifts} sites={sites} officers={officers}
            tenantId={tenantParam} canWrite={canWrite}
            siteFilter={siteFilter}
            onChanged={() => void load()}
            onClose={() => setOpenDate(null)}
            onOpenException={(modal) => setExceptionModal(modal)}
          />
        )}
      </Drawer>

      <Drawer
        open={genOpen}
        onClose={() => setGenOpen(false)}
        title="自動生成排班"
        description="按公平規則 (員工目前班次最少優先) 把缺人的班別自動排上。"
      >
        {genOpen && (
          <GenerateForm
            cursor={cursor}
            sites={sites}
            tenantId={tenantParam}
            defaultSite={siteFilter || undefined}
            onDone={() => { setGenOpen(false); void load(); }}
            onClose={() => setGenOpen(false)}
          />
        )}
      </Drawer>

      <Drawer
        open={!!exceptionModal}
        onClose={() => setExceptionModal(null)}
        title={exceptionModal?.mode === "edit" ? "編輯例外日" : "新增例外日"}
        description={exceptionModal?.mode === "edit" ? exceptionModal.row.exception_date : exceptionModal?.mode === "new" ? exceptionModal.date : ""}
      >
        {exceptionModal && (
          <ExceptionForm
            mode={exceptionModal.mode}
            row={exceptionModal.mode === "edit" ? exceptionModal.row : null}
            seedDate={exceptionModal.mode === "new" ? exceptionModal.date : undefined}
            sites={sites}
            tenantId={tenantParam} canWrite={canWrite}
            defaultSite={siteFilter || undefined}
            onSaved={() => { setExceptionModal(null); void load(); }}
            onClose={() => setExceptionModal(null)}
          />
        )}
      </Drawer>
    </>
  );
}

function DayDrawer({ date, entries, exceptions, shifts, sites, officers, tenantId, canWrite, siteFilter, onChanged, onClose, onOpenException }: {
  date: string;
  entries: RosterEntry[];
  exceptions: ScheduleException[];
  shifts: ShiftRow[]; sites: SiteRow[]; officers: OfficerPickerRow[];
  tenantId: string | null; canWrite: boolean;
  siteFilter: string;
  onChanged: () => void; onClose: () => void;
  onOpenException: (m: { mode: "new"; date: string } | { mode: "edit"; row: ScheduleException }) => void;
}): React.ReactElement {
  const [edit, setEdit] = useState<{ kind: "new" } | { kind: "edit"; entry: RosterEntry } | null>(null);

  return (
    <div className="space-y-4">
      {exceptions.length > 0 && (
        <div className="space-y-1">
          <div className="text-xs uppercase tracking-wider text-text-muted">例外日</div>
          <div className="divide-y divide-border rounded border border-border">
            {exceptions.map((ex) => (
              <button key={ex.id}
                onClick={() => onOpenException({ mode: "edit", row: ex })}
                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-bg-hover">
                <div className="min-w-0">
                  <div className="truncate text-text-primary">
                    <span className={`badge ${KIND_RIBBON[ex.kind]} mr-2`}>{KIND_LABEL[ex.kind]}</span>
                    {ex.label}
                  </div>
                  <div className="truncate text-xs text-text-secondary">
                    {ex.site_name ?? "全 tenant"}
                    {ex.extends_until && ex.extends_until !== ex.exception_date ? <> ・ {ex.exception_date}–{ex.extends_until}</> : null}
                    {ex.auto_skip ? " ・ 自動跳過排班" : null}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="text-xs uppercase tracking-wider text-text-muted">當日排班</div>
      {entries.length === 0 ? (
        <div className="rounded border border-border bg-bg-surface p-4 text-sm text-text-secondary">
          這天還沒有排班。
        </div>
      ) : (
        <div className="divide-y divide-border rounded border border-border">
          {entries.map((e) => (
            <button key={e.id}
              onClick={() => setEdit({ kind: "edit", entry: e })}
              className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-bg-hover">
              <div className="min-w-0">
                <div className="truncate font-medium text-text-primary">
                  {e.shift_name ?? "—"} <span className="ml-1 font-mono text-xs text-text-secondary">{e.shift_start}–{e.shift_end}</span>
                </div>
                <div className="truncate text-xs text-text-secondary">
                  {e.officer_name ?? e.officer_id} ・ {e.site_name ?? e.site_id}
                  {e.notes ? <> ・ {e.notes}</> : null}
                </div>
              </div>
              <span className={STATUS_BADGE[e.status]}>{STATUS_LABEL[e.status]}</span>
            </button>
          ))}
        </div>
      )}

      {canWrite && (
        <div className="flex flex-wrap justify-end gap-2">
          <button onClick={() => onOpenException({ mode: "new", date })} className="btn-secondary text-sm">
            <Plus className="h-4 w-4" /> 新增例外日
          </button>
          <button onClick={() => setEdit({ kind: "new" })} className="btn-primary text-sm">
            <Plus className="h-4 w-4" /> 新增排班
          </button>
        </div>
      )}

      <Drawer
        open={!!edit}
        onClose={() => setEdit(null)}
        title={edit?.kind === "edit" ? "編輯排班" : "新增排班"}
        description={`日期 ${date}`}
      >
        {edit && (
          <RosterForm
            mode={edit.kind}
            entry={edit.kind === "edit" ? edit.entry : null}
            date={date}
            shifts={shifts} sites={sites} officers={officers}
            tenantId={tenantId} canWrite={canWrite}
            defaultSite={siteFilter || undefined}
            onSaved={() => { setEdit(null); onChanged(); }}
            onClose={() => setEdit(null)}
          />
        )}
      </Drawer>

      <div className="flex justify-end pt-2">
        <button onClick={onClose} className="btn-secondary text-sm">關閉</button>
      </div>
    </div>
  );
}

function RosterForm({ mode, entry, date, shifts, sites, officers, tenantId, canWrite, defaultSite, onSaved, onClose }: {
  mode: "new" | "edit";
  entry: RosterEntry | null;
  date: string;
  shifts: ShiftRow[]; sites: SiteRow[]; officers: OfficerPickerRow[];
  tenantId: string | null; canWrite: boolean;
  defaultSite?: string;
  onSaved: () => void; onClose: () => void;
}): React.ReactElement {
  const [shiftId, setShiftId] = useState(entry?.shift_id ?? shifts[0]?.id ?? "");
  const [officerId, setOfficerId] = useState(entry?.officer_id ?? officers[0]?.id ?? "");
  const [siteId, setSiteId] = useState(entry?.site_id ?? defaultSite ?? sites[0]?.id ?? "");
  const [status, setStatus] = useState<RosterStatus>(entry?.status ?? "scheduled");
  const [notes, setNotes] = useState(entry?.notes ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    if (!canWrite) return;
    setBusy(true); setErr(null);
    try {
      if (mode === "new") {
        await createRoster({
          shift_id: shiftId, officer_id: officerId, site_id: siteId,
          work_date: date, notes: notes.trim() || null, status,
        }, tenantId);
      } else if (entry) {
        await patchRoster(entry.id, {
          status, officer_id: officerId,
          notes: notes.trim() || null,
        }, tenantId);
      }
      onSaved();
    } catch (e) { setErr(friendlyRosterError(e)); }
    finally { setBusy(false); }
  };
  const dlg = useConfirmDialog();
  const cancel = async () => {
    if (!entry) return;
    if (!(await dlg.confirm("確定要取消這筆排班？", { destructive: true, okLabel: "取消排班" }))) return;
    setBusy(true); setErr(null);
    try { await cancelRoster(entry.id, tenantId); onSaved(); }
    catch (e) { setErr(friendlyRosterError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Field label="班別" required>
        <select className="select" value={shiftId} onChange={(e) => setShiftId(e.target.value)} disabled={mode === "edit"}>
          {shifts.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.start_time}–{s.end_time})</option>)}
        </select>
      </Field>
      <Field label="案場" required>
        <select className="select" value={siteId} onChange={(e) => setSiteId(e.target.value)} disabled={mode === "edit"}>
          {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </Field>
      <Field label="員工" required>
        <select className="select" value={officerId} onChange={(e) => setOfficerId(e.target.value)}>
          {officers.map((o) => <option key={o.id} value={o.id}>{o.display_name}{o.status !== "active" ? `（${o.status}）` : ""}</option>)}
        </select>
      </Field>
      <Field label="狀態">
        <select className="select" value={status} onChange={(e) => setStatus(e.target.value as RosterStatus)}>
          <option value="scheduled">已排班</option>
          <option value="confirmed">已確認</option>
          <option value="swapped">已換班</option>
        </select>
      </Field>
      <Field label="備註" hint="最多 500 字"><input className="input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="（選填）" /></Field>
      {err && <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">{err}</div>}
      <div className="flex flex-row items-center justify-between gap-2">
        <div>{mode === "edit" && entry && entry.status !== "cancelled" && canWrite && (
          <button className="btn-danger text-sm" onClick={() => void cancel()} disabled={busy}><Trash2 className="h-4 w-4" /> 取消排班</button>
        )}</div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="btn-secondary text-sm" disabled={busy}>取消</button>
          {canWrite && (
            <button onClick={() => void save()} className="btn-primary text-sm"
              disabled={busy || !shiftId || !officerId || !siteId}>
              {busy ? "儲存中…" : "儲存"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ExceptionForm({ mode, row, seedDate, sites, tenantId, canWrite, defaultSite, onSaved, onClose }: {
  mode: "new" | "edit";
  row: ScheduleException | null;
  seedDate?: string;
  sites: SiteRow[];
  tenantId: string | null; canWrite: boolean;
  defaultSite?: string;
  onSaved: () => void; onClose: () => void;
}): React.ReactElement {
  const [exceptionDate, setExceptionDate] = useState(row?.exception_date ?? seedDate ?? "");
  const [extendsUntil, setExtendsUntil] = useState(row?.extends_until ?? "");
  const [siteId, setSiteId] = useState(row?.site_id ?? defaultSite ?? "");  // "" = tenant-wide
  const [kind, setKind] = useState<ExceptionKind>(row?.kind ?? "holiday");
  const [autoSkip, setAutoSkip] = useState<boolean>(row ? row.auto_skip === 1 : true);
  const [label, setLabel] = useState(row?.label ?? "");
  const [notes, setNotes] = useState(row?.notes ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    if (!canWrite) return;
    setBusy(true); setErr(null);
    try {
      if (mode === "new") {
        await createException({
          exception_date: exceptionDate, label: label.trim(),
          kind, site_id: siteId || null,
          extends_until: extendsUntil || null,
          auto_skip: autoSkip,
          notes: notes.trim() || null,
        }, tenantId);
      } else if (row) {
        await patchException(row.id, {
          label: label.trim(), kind, auto_skip: autoSkip,
          extends_until: extendsUntil || null,
          notes: notes.trim() || null,
        }, tenantId);
      }
      onSaved();
    } catch (e) { setErr(friendlyExceptionsError(e)); }
    finally { setBusy(false); }
  };
  const dlg = useConfirmDialog();
  const remove = async () => {
    if (!row) return;
    if (!(await dlg.confirm("確定要刪除這筆例外日？", { destructive: true, okLabel: "刪除" }))) return;
    setBusy(true); setErr(null);
    try { await deleteException(row.id, tenantId); onSaved(); }
    catch (e) { setErr(friendlyExceptionsError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="日期" required>
          <input className="input font-mono" type="date" value={exceptionDate}
            disabled={mode === "edit"}
            onChange={(e) => setExceptionDate(e.target.value)} />
        </Field>
        <Field label="持續到" hint="多日連假留空 = 單日">
          <input className="input font-mono" type="date" value={extendsUntil}
            onChange={(e) => setExtendsUntil(e.target.value)} />
        </Field>
      </div>
      <Field label="案場" hint="留空 = 全 tenant 都套用">
        <select className="select" value={siteId} onChange={(e) => setSiteId(e.target.value)} disabled={mode === "edit"}>
          <option value="">全 tenant</option>
          {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </Field>
      <Field label="類別">
        <select className="select" value={kind} onChange={(e) => setKind(e.target.value as ExceptionKind)}>
          <option value="holiday">假日</option>
          <option value="typhoon">颱風</option>
          <option value="closure">案場關閉</option>
          <option value="extra_coverage">加開</option>
          <option value="custom">其他</option>
        </select>
      </Field>
      <Field label="名稱" required hint="例：元旦 / 中颱海葵停班"><input className="input" value={label} onChange={(e) => setLabel(e.target.value)} /></Field>
      <Field label="備註"><input className="input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="（選填）" /></Field>
      <label className="flex items-center gap-2 text-sm text-text-secondary">
        <input type="checkbox" checked={autoSkip} onChange={(e) => setAutoSkip(e.target.checked)} />
        <span>自動跳過排班 — 月曆上這天的「人力缺口」徽章不會出現，自動排班 / 巡邏視窗也會跳過。</span>
      </label>
      {err && <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">{err}</div>}
      <div className="flex flex-row items-center justify-between gap-2">
        <div>{mode === "edit" && row && canWrite && (
          <button className="btn-danger text-sm" onClick={() => void remove()} disabled={busy}><Trash2 className="h-4 w-4" /> 刪除</button>
        )}</div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="btn-secondary text-sm" disabled={busy}>取消</button>
          {canWrite && (
            <button onClick={() => void save()} className="btn-primary text-sm"
              disabled={busy || !exceptionDate || !label.trim()}>
              {busy ? "儲存中…" : "儲存"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function GenerateForm({ cursor, sites, tenantId, defaultSite, onDone, onClose }: {
  cursor: Date;
  sites: SiteRow[];
  tenantId: string | null;
  defaultSite?: string;
  onDone: () => void; onClose: () => void;
}): React.ReactElement {
  const monthRange = useMemo(() => {
    const first = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth(), 1));
    const last = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 0));
    return { from: ymd(first), to: ymd(last) };
  }, [cursor]);
  const [from, setFrom] = useState(monthRange.from);
  const [to, setTo] = useState(monthRange.to);
  const [siteId, setSiteId] = useState(defaultSite ?? "");
  const [overwrite, setOverwrite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [preview, setPreview] = useState<GenerateRosterResponse | null>(null);

  const run = async (dryRun: boolean) => {
    setBusy(true); setErr(null);
    try {
      const r = await generateRoster(
        { from, to, site_id: siteId || null, dry_run: dryRun, overwrite_existing: overwrite },
        tenantId,
      );
      setPreview(r);
      if (!dryRun) onDone();
    } catch (e) { setErr(friendlyRosterError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="從" required>
          <input className="input font-mono" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field label="到" required>
          <input className="input font-mono" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </Field>
      </div>
      <Field label="案場" hint="留空 = 全 tenant 所有案場">
        <select className="select" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
          <option value="">全部</option>
          {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </Field>
      <label className="flex items-center gap-2 text-sm text-text-secondary">
        <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} />
        <span>包含已部份排好的格子（仍會避免和現有 entry 重疊）。</span>
      </label>
      {err && <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">{err}</div>}

      {preview && (
        <div className="rounded border border-border bg-bg-surface p-3 text-sm space-y-2">
          <div className="font-medium">
            預覽：{preview.summary.proposed ?? preview.summary.created ?? 0} 個排班 ・ 跳過 {preview.summary.skipped} 個
            {preview.summary.officers_used ? ` ・ 用到 ${preview.summary.officers_used} 名員工` : ""}
            {preview.dry_run ? "（試算）" : "（已寫入）"}
          </div>
          {preview.skipped.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-text-secondary">跳過的格子 ({preview.skipped.length})</summary>
              <ul className="mt-1 max-h-40 overflow-y-auto space-y-0.5">
                {preview.skipped.slice(0, 60).map((s, i) => (
                  <li key={i} className="text-text-muted">
                    <span className="font-mono">{s.work_date}</span> ・ {s.shift_name} ・ {reasonZh(s.reason)} ({s.filled}/{s.needed})
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      <div className="flex flex-row items-center justify-end gap-2">
        <button onClick={onClose} className="btn-secondary text-sm" disabled={busy}>關閉</button>
        <button onClick={() => void run(true)} className="btn-secondary text-sm" disabled={busy}>
          {busy ? "計算中…" : "試算"}
        </button>
        <button onClick={() => void run(false)} className="btn-primary text-sm" disabled={busy || !from || !to}>
          {busy ? "生成中…" : "確定生成"}
        </button>
      </div>
    </div>
  );
}

function reasonZh(r: "no_eligible_officer" | "skipped_by_exception" | "already_filled"): string {
  switch (r) {
    case "no_eligible_officer": return "沒有可分配的員工";
    case "skipped_by_exception": return "例外日跳過";
    case "already_filled": return "已排滿";
  }
}

function ymd(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}
function firstOfMonth(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}
function addMonths(d: Date, n: number): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1));
}
// 月格 6×7：從 cursor 月份的第一天向前回到該週的星期一，向後 41 天。
function monthGridRange(cursor: Date): { from: string; to: string } {
  const first = firstOfMonth(cursor);
  const dow = (first.getUTCDay() + 6) % 7;  // 0=Mon...6=Sun
  const from = new Date(first.getTime());
  from.setUTCDate(first.getUTCDate() - dow);
  const to = new Date(from.getTime());
  to.setUTCDate(from.getUTCDate() + 41);
  return { from: ymd(from), to: ymd(to) };
}

void Users;
